/**
 * Model Context Protocol (MCP) Server
 * 
 * Core server implementation that exposes Aideline Pro CRM
 * to MCP-compatible AI agents and tools (Claude, n8n, etc.)
 */

import {
  MCPRequest,
  MCPResponse,
  MCPNotification,
  MCPError,
  MCPServerConfig,
  MCPTool,
  MCPResource,
  MCPPrompt,
  MCPExecutionContext,
  MCPOrgConfig,
  MCPHealthStatus,
  MCPLogEntry,
  MCPMetrics
} from '../types';
import { MCPToolRegistry } from './MCPToolRegistry';
import { MCPResourceRegistry } from './MCPResourceRegistry';
import { MCPPromptRegistry } from './MCPPromptRegistry';
import { MCPOrgContext } from './MCPOrgContext';
import { MCPLogger } from './MCPLogger';

export class MCPServer {
  private config: MCPServerConfig;
  private toolRegistry: MCPToolRegistry;
  private resourceRegistry: MCPResourceRegistry;
  private promptRegistry: MCPPromptRegistry;
  private orgContext: MCPOrgContext;
  private logger: MCPLogger;
  private metrics: MCPMetrics;
  private isRunning: boolean = false;
  private startTime: Date;

  constructor(config?: Partial<MCPServerConfig>) {
    this.config = {
      name: 'aideline-pro-mcp',
      version: '1.0.0',
      description: 'Aideline Pro CRM - Model Context Protocol Server',
      capabilities: {
        resources: {
          subscribe: true,
          listChanged: true
        },
        tools: {
          listChanged: true
        },
        prompts: {
          listChanged: true
        },
        logging: true
      },
      ...config
    };

    this.toolRegistry = new MCPToolRegistry();
    this.resourceRegistry = new MCPResourceRegistry();
    this.promptRegistry = new MCPPromptRegistry();
    this.orgContext = new MCPOrgContext();
    this.logger = new MCPLogger();
    this.startTime = new Date();

    this.metrics = {
      totalRequests: 0,
      successfulRequests: 0,
      failedRequests: 0,
      averageResponseTime: 0,
      toolUsageStats: {},
      orgUsageStats: {}
    };

    this.logger.info('MCP Server initialized', { config: this.config });
  }

  /**
   * Start the MCP Server (idempotent - safe to call multiple times)
   */
  async start(): Promise<void> {
    if (this.isRunning) {
      this.logger.debug('MCP Server is already running');
      return; // Already running, no-op
    }

    this.logger.info('Starting MCP Server...');
    
    // Initialize registries
    await this.toolRegistry.initialize();
    await this.resourceRegistry.initialize();
    await this.promptRegistry.initialize();
    
    this.isRunning = true;
    this.startTime = new Date();
    
    this.logger.info('MCP Server started successfully', {
      version: this.config.version,
      tools: this.toolRegistry.getToolCount(),
      resources: this.resourceRegistry.getResourceCount(),
      prompts: this.promptRegistry.getPromptCount()
    });
  }

  /**
   * Stop the MCP Server
   */
  async stop(): Promise<void> {
    if (!this.isRunning) {
      return;
    }

    this.logger.info('Stopping MCP Server...');
    this.isRunning = false;
    this.logger.info('MCP Server stopped');
  }

  /**
   * Handle incoming MCP request
   */
  async handleRequest(request: MCPRequest): Promise<MCPResponse> {
    const startTime = Date.now();
    this.metrics.totalRequests++;

    try {
      this.logger.debug('Handling MCP request', { 
        method: request.method, 
        id: request.id 
      });

      // Validate request
      if (!this.isValidRequest(request)) {
        throw this.createError(-32600, 'Invalid Request');
      }

      // Route to appropriate handler
      let result: any;
      
      switch (request.method) {
        case 'initialize':
          result = await this.handleInitialize(request.params);
          break;
        
        case 'tools/list':
          result = await this.handleToolsList(request.params);
          break;
        
        case 'tools/call':
          result = await this.handleToolCall(request.params);
          break;
        
        case 'resources/list':
          result = await this.handleResourcesList(request.params);
          break;
        
        case 'resources/read':
          result = await this.handleResourceRead(request.params);
          break;
        
        case 'prompts/list':
          result = await this.handlePromptsList(request.params);
          break;
        
        case 'prompts/get':
          result = await this.handlePromptGet(request.params);
          break;
        
        case 'logging/setLevel':
          result = await this.handleLoggingSetLevel(request.params);
          break;
        
        default:
          throw this.createError(-32601, `Method not found: ${request.method}`);
      }

      // Success
      this.metrics.successfulRequests++;
      const duration = Date.now() - startTime;
      this.updateMetrics(duration);

      return {
        jsonrpc: '2.0',
        id: request.id,
        result
      };

    } catch (error: any) {
      this.metrics.failedRequests++;
      
      this.logger.error('Error handling MCP request', {
        method: request.method,
        error: error.message
      });

      return {
        jsonrpc: '2.0',
        id: request.id,
        error: error.code ? error : this.createError(-32603, error.message)
      };
    }
  }

  /**
   * Initialize - First method called by MCP clients
   */
  private async handleInitialize(params: any): Promise<any> {
    return {
      protocolVersion: '2024-11-05',
      capabilities: this.config.capabilities,
      serverInfo: {
        name: this.config.name,
        version: this.config.version,
        description: this.config.description
      }
    };
  }

  /**
   * List available tools
   */
  private async handleToolsList(params: any): Promise<any> {
    const context = await this.createContext(params);
    const { cursor, limit } = params || {};
    const { tools, nextCursor } = await this.toolRegistry.listTools(context, cursor, limit);
    
    return {
      tools: tools.map(tool => ({
        name: tool.name,
        description: tool.description,
        inputSchema: tool.inputSchema
      })),
      nextCursor
    };
  }

  /**
   * Execute a tool
   */
  private async handleToolCall(params: any): Promise<any> {
    const { name, arguments: args } = params;
    const context = await this.createContext(params);
    
    // Track tool usage
    this.metrics.toolUsageStats[name] = (this.metrics.toolUsageStats[name] || 0) + 1;
    this.metrics.orgUsageStats[context.orgId] = (this.metrics.orgUsageStats[context.orgId] || 0) + 1;
    
    this.logger.info('Executing tool', { 
      tool: name, 
      orgId: context.orgId,
      userId: context.userId 
    });

    const result = await this.toolRegistry.executeTool(name, args, context);
    
    return {
      content: [
        {
          type: 'text',
          text: typeof result === 'string' ? result : JSON.stringify(result, null, 2)
        }
      ]
    };
  }

  /**
   * List available resources
   */
  private async handleResourcesList(params: any): Promise<any> {
    const context = await this.createContext(params);
    const { cursor, limit } = params || {};
    const { resources, nextCursor } = await this.resourceRegistry.listResources(context, cursor, limit);
    
    return { resources, nextCursor };
  }

  /**
   * Read a resource
   */
  private async handleResourceRead(params: any): Promise<any> {
    const { uri } = params;
    const context = await this.createContext(params);
    
    const content = await this.resourceRegistry.readResource(uri, context);
    
    return {
      contents: [content]
    };
  }

  /**
   * List available prompts
   */
  private async handlePromptsList(params: any): Promise<any> {
    const context = await this.createContext(params);
    const prompts = await this.promptRegistry.listPrompts(context);
    
    return { prompts };
  }

  /**
   * Get a prompt
   */
  private async handlePromptGet(params: any): Promise<any> {
    const { name, arguments: args } = params;
    const context = await this.createContext(params);
    
    return await this.promptRegistry.getPrompt(name, args || {}, context);
  }

  /**
   * Set logging level
   */
  private async handleLoggingSetLevel(params: any): Promise<any> {
    const { level } = params;
    this.logger.setLevel(level);
    return { success: true };
  }

  /**
   * Create execution context from request params
   */
  private async createContext(params: any): Promise<MCPExecutionContext> {
    const { orgId, userId } = params._context || {};
    
    if (!orgId || !userId) {
      throw this.createError(-32602, 'Missing required context: orgId and userId');
    }

    return await this.orgContext.createContext(orgId, userId);
  }

  /**
   * Validate MCP request format
   */
  private isValidRequest(request: MCPRequest): boolean {
    return (
      request.jsonrpc === '2.0' &&
      typeof request.method === 'string' &&
      (request.id !== undefined)
    );
  }

  /**
   * Create MCP error object
   */
  private createError(code: number, message: string, data?: any): MCPError {
    return { code, message, data };
  }

  /**
   * Update metrics
   */
  private updateMetrics(duration: number): void {
    const totalTime = this.metrics.averageResponseTime * (this.metrics.successfulRequests - 1);
    this.metrics.averageResponseTime = (totalTime + duration) / this.metrics.successfulRequests;
  }

  /**
   * Get server health status
   */
  async getHealthStatus(): Promise<MCPHealthStatus> {
    const uptime = Date.now() - this.startTime.getTime();
    
    return {
      status: this.isRunning ? 'healthy' : 'unhealthy',
      uptime,
      version: this.config.version,
      capabilities: this.config.capabilities,
      metrics: this.metrics,
      checks: {
        database: true, // TODO: Actual health checks
        firebase: true,
        openai: true
      }
    };
  }

  /**
   * Get server configuration
   */
  getConfig(): MCPServerConfig {
    return { ...this.config };
  }

  /**
   * Get metrics
   */
  getMetrics(): MCPMetrics {
    return { ...this.metrics };
  }

  /**
   * Register a new tool
   */
  registerTool(tool: MCPTool): void {
    this.toolRegistry.registerTool(tool);
    this.logger.info('Tool registered', { name: tool.name });
  }

  /**
   * Register a new resource
   */
  registerResource(resource: MCPResource): void {
    this.resourceRegistry.registerResource(resource, async (uri) => {
      return {
        uri,
        mimeType: 'text/plain',
        text: 'Resource content placeholder'
      };
    });
    this.logger.info('Resource registered', { uri: resource.uri });
  }

  /**
   * Register a new prompt
   */
  registerPrompt(prompt: MCPPrompt, getMessages: any): void {
    this.promptRegistry.registerPrompt(prompt, getMessages);
    this.logger.info('Prompt registered', { name: prompt.name });
  }
}

// Singleton instance
let serverInstance: MCPServer | null = null;

export function getMCPServer(): MCPServer {
  if (!serverInstance) {
    serverInstance = new MCPServer();
  }
  return serverInstance;
}

export function createMCPServer(config?: Partial<MCPServerConfig>): MCPServer {
  serverInstance = new MCPServer(config);
  return serverInstance;
}

